import React, { useEffect, useState, useRef } from 'react';
import { Table, Typography, Button, Tag, Space, Input, DatePicker, message, Tooltip, Modal, Grid, Form, Select, Row, Col, Card, Dropdown } from 'antd';
import { PlusOutlined, EyeOutlined, FileTextOutlined, FilePdfOutlined, DeleteOutlined, MailOutlined, CopyOutlined, FilterOutlined, MoreOutlined, DownloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { userService, companiesService, receiversService } from '../../services/api';

const { Title } = Typography;
const { useBreakpoint } = Grid;

const csvEscape = (value) => {
  const raw = value == null ? '' : String(value);
  const escaped = raw.replace(/"/g, '""');
  return `"${escaped}"`;
};

const buildCsv = (headers, rows) => {
  const headerLine = headers.map(csvEscape).join(',');
  const dataLines = rows.map((r) => r.map(csvEscape).join(','));
  return `\uFEFF${[headerLine, ...dataLines].join('\r\n')}`;
};

const downloadCsv = (csvContent, filename) => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

const UserInvoices = () => {
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportingCsv, setExportingCsv] = useState(false);
  const [bottomPaginationVisible, setBottomPaginationVisible] = useState(false);
  const topPaginationRef = useRef(null);

  // Filtros hacia backend
  const [uuidFilter, setUuidFilter] = useState('');
  const [dateRange, setDateRange] = useState([]);
  const [statusFilter, setStatusFilter] = useState('timbrada'); // Default: timbrada (excluye borrador)
  const [companyFilter, setCompanyFilter] = useState(null);
  const [paymentMethodFilter, setPaymentMethodFilter] = useState(null); // Nuevo filtro: PUE o PPD
  const [clientFilter, setClientFilter] = useState(null); // Nuevo filtro: Cliente
  const [companies, setCompanies] = useState([]);
  const [clients, setClients] = useState([]); // Lista de clientes para el select

  // Estado para envío de correo
  const [emailModalVisible, setEmailModalVisible] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [currentInvoice, setCurrentInvoice] = useState(null);
  const [emailForm] = Form.useForm();
  const searchTimeoutRef = useRef(null);
  
  // Estados para controlar el loading de cada fila y tipo de acción
  const [downloadingXml, setDownloadingXml] = useState({});
  const [downloadingPdf, setDownloadingPdf] = useState({});
  
  // Estado para previsualización PDF
  const [previewVisible, setPreviewVisible] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setBottomPaginationVisible(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      },
      { threshold: 0 }
    );

    if (topPaginationRef.current) {
      observer.observe(topPaginationRef.current);
    }
    
    // Fetch companies for filter
    fetchCompanies();
    // Fetch clients for filter
    fetchClients();

    return () => observer.disconnect();
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await companiesService.getCompanies();
      setCompanies(response.data.companies || []);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const fetchClients = async (search = '') => {
    try {
      const response = await receiversService.getReceivers(search);
      setClients(response.data || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const handleClientSearch = (value) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      fetchClients(value);
    }, 500);
  };

  const buildFilters = (uuid, range, status, company, paymentMethod, client) => {
    const f = {};
    if (uuid) f.uuid = uuid;
    if (Array.isArray(range) && range.length === 2 && range[0] && range[1]) {
      f.date_from = range[0].format('YYYY-MM-DD');
      f.date_to = range[1].format('YYYY-MM-DD');
    }
    // Si status es null o vacío, backend podría devolver todo. 
    // El usuario pidió ignorar borradores a menos que se seleccione explícitamente.
    // Manejaremos esto asegurando que status tenga valor o filtrando en front si el backend no soporta exclusión.
    // Asumiremos que el backend filtra por lo que enviamos.
    if (status) f.status = status;
    else f.status = 'timbrada,cancelada'; // Default si no se selecciona nada: excluir borrador

    if (company) f.company_id = company;
    if (paymentMethod) f.metodo_pago = paymentMethod;
    if (client) f.receiver_id = client;
    return f;
  };

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  const fetchInvoices = async (filters = {}, page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      // Combinar filtros actuales con los nuevos
      const currentFilters = buildFilters(uuidFilter, dateRange, statusFilter, companyFilter, paymentMethodFilter, clientFilter);
      const finalFilters = { ...currentFilters, ...filters, limit: pageSize };
      
      const response = await userService.getInvoices(page, 'N', finalFilters);
      
      setInvoices(response.data.invoices || []);
      setPagination({
        current: page,
        pageSize: pageSize,
        total: response.data.total || 0
      });
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices({}, 1, 10);
  }, []);

  const exportInvoicesCsv = async () => {
    setExportingCsv(true);
    try {
      const pageSize = 100;
      const filters = buildFilters(uuidFilter, dateRange, statusFilter, companyFilter, paymentMethodFilter, clientFilter);
      const all = [];
      let page = 1;
      let total = null;

      while (true) {
        const resp = await userService.getInvoices(page, 'N', { ...filters, limit: pageSize });
        const list = resp?.data?.invoices || [];
        if (total == null) total = resp?.data?.total ?? list.length;
        all.push(...list);
        if (list.length === 0) break;
        if (all.length >= total) break;
        page += 1;
        if (page > 200) break;
      }

      if (all.length === 0) {
        message.info('No hay facturas para exportar con los filtros actuales.');
        return;
      }

      const headers = [
        'Serie',
        'Folio',
        'UUID',
        'RFC Cliente',
        'Cliente',
        'Empresa',
        'Método de pago',
        'Moneda',
        'Total',
        'Pagado (PPD)',
        'Saldo pendiente (PPD)',
        'Estatus',
        'Fecha creación',
        'Fecha timbrado',
      ];

      const rows = all.map((row) => {
        const totalNum = Number(row?.total || 0);
        const paid = Number(row?.monto_pagado || 0);
        const saldo = row?.saldo_pendiente != null ? Number(row.saldo_pendiente) : Math.max(totalNum - paid, 0);
        return [
          row?.serie || '',
          row?.folio || '',
          row?.uuid || row?.folio_fiscal || '',
          row?.receiver?.tax_id || '',
          row?.receiver?.tax_name || '',
          row?.company?.tax_name || row?.company?.business_name || '',
          row?.metodo_pago || '',
          row?.moneda || '',
          totalNum.toFixed(2),
          row?.metodo_pago === 'PPD' ? paid.toFixed(2) : '',
          row?.metodo_pago === 'PPD' ? saldo.toFixed(2) : '',
          row?.status || '',
          row?.created_at ? new Date(row.created_at).toISOString() : '',
          row?.fecha_timbrado ? new Date(row.fecha_timbrado).toISOString() : '',
        ];
      });

      const csv = buildCsv(headers, rows);
      const now = new Date();
      const stamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      downloadCsv(csv, `facturas_${stamp}.csv`);
      message.success(`CSV exportado (${all.length} facturas).`);
    } catch (e) {
      message.error('No se pudo exportar el CSV.');
    } finally {
      setExportingCsv(false);
    }
  };

  const handleTableChange = (newPagination) => {
    fetchInvoices({}, newPagination.current, newPagination.pageSize);
  };

  const downloadResponseFile = (response, defaultFilename) => {
    const contentType = response.headers['content-type'] || 'application/octet-stream';
    const disposition = response.headers['content-disposition'] || '';
    const match = /filename="?([^";]+)"?/i.exec(disposition);
    const filename = match?.[1] || defaultFilename;

    let blob;
    const data = response.data;
    if (data instanceof Blob) {
      blob = data;
    } else if (data instanceof ArrayBuffer) {
      blob = new Blob([data], { type: contentType });
    } else {
      blob = new Blob([data], { type: contentType });
    }

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const downloadBase64Pdf = (base64Content, filename) => {
    if (!base64Content) {
      message.error('El PDF no se recibió correctamente.');
      return;
    }
    const cleaned = base64Content.includes(',')
      ? base64Content.split(',').pop()
      : base64Content;
    const byteChars = atob(cleaned);
    const byteNums = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      byteNums[i] = byteChars.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNums);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'Factura.pdf';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleQuickDownloadXml = async (record) => {
    try {
      setDownloadingXml(prev => ({ ...prev, [record.id]: true }));
      const resp = await userService.downloadInvoiceXml(record.id);
      const prefix = record?.tipo_comprobante === 'N' ? 'Nomina_' : 'CFDI_';
      const filename = `${prefix}${record?.serie || ''}_${record?.folio || ''}_${record?.uuid || ''}.xml`;
      downloadResponseFile(resp, filename);
    } catch (err) {
      message.error('No se pudo descargar el XML.');
    } finally {
      setDownloadingXml(prev => ({ ...prev, [record.id]: false }));
    }
  };

  const handleQuickDownloadPdf = async (record) => {
    try {
      setDownloadingPdf(prev => ({ ...prev, [record.id]: true }));
      if (record.status === 'timbrada') {
        const result = await userService.generateInvoicePdf(record.id, {});
        const base64 = result?.data?.pdf_content;
        const filename = result?.data?.filename;
        if (!base64) throw new Error('No se recibió el PDF');
        downloadBase64Pdf(base64, filename);
      } else {
        const result = await userService.previewInvoicePdf(record.id, {});
        const base64 = result?.data?.pdf?.pdf_content;
        if (!base64) throw new Error('No se recibió el PDF');

        const cleaned = base64.includes(',') ? base64.split(',').pop() : base64;
        const byteChars = atob(cleaned);
        const byteNums = new Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) {
          byteNums[i] = byteChars.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNums);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        setPdfUrl(url);
        setPreviewVisible(true);
      }
    } catch (err) {
      console.error(err);
      message.error('No se pudo obtener el PDF.');
    } finally {
      setDownloadingPdf(prev => ({ ...prev, [record.id]: false }));
    }
  };

  const handleUuidChange = (e) => {
    const value = e.target.value;
    setUuidFilter(value);
    // Reiniciar a página 1 al cambiar filtros
    fetchInvoices(buildFilters(value, dateRange, statusFilter, companyFilter, paymentMethodFilter, clientFilter), 1, pagination.pageSize);
  };

  const handleDateChange = (values) => {
    const range = values || [];
    setDateRange(range);
    fetchInvoices(buildFilters(uuidFilter, range, statusFilter, companyFilter, paymentMethodFilter, clientFilter), 1, pagination.pageSize);
  };

  const handleStatusChange = (value) => {
    setStatusFilter(value);
    fetchInvoices(buildFilters(uuidFilter, dateRange, value, companyFilter, paymentMethodFilter, clientFilter), 1, pagination.pageSize);
  };

  const handleCompanyChange = (value) => {
    setCompanyFilter(value);
    fetchInvoices(buildFilters(uuidFilter, dateRange, statusFilter, value, paymentMethodFilter, clientFilter), 1, pagination.pageSize);
  };

  const handlePaymentMethodChange = (value) => {
    setPaymentMethodFilter(value);
    fetchInvoices(buildFilters(uuidFilter, dateRange, statusFilter, companyFilter, value, clientFilter), 1, pagination.pageSize);
  };

  const handleClientChange = (value) => {
    setClientFilter(value);
    fetchInvoices(buildFilters(uuidFilter, dateRange, statusFilter, companyFilter, paymentMethodFilter, value), 1, pagination.pageSize);
  };

  const handleEmailClick = (record) => {
    setCurrentInvoice(record);
    const clientEmail = record?.receiver?.email || '';
    emailForm.setFieldsValue({ recipient_email: clientEmail });
    
    // Default subject and body
    const serieFolio = (record?.serie || '') + (record?.folio || '');
    const companyName = record?.company?.business_name || 'Mi Factura';
    
    const defaultSubject = `Factura ${serieFolio} de ${companyName}`;
    const defaultBody = `Adjunto encontrará los archivos XML y PDF de su factura.\n\nTotal: $${Number(record?.total || 0).toFixed(2)} ${record?.moneda || 'MXN'}\n\nSaludos cordiales.`;
    
    emailForm.setFieldsValue({
      subject: defaultSubject,
      body: defaultBody
    });
    
    setEmailModalVisible(true);
  };

  const handleSendEmail = async (values) => {
    if (!currentInvoice) return;
    
    setSendingEmail(true);
    try {
      await userService.sendInvoiceEmail(currentInvoice.id, {
        recipients: [values.recipient_email],
        subject: values.subject,
        body: values.body
      });
      message.success('Correo enviado exitosamente');
      setEmailModalVisible(false);
      setCurrentInvoice(null);
    } catch (err) {
      console.error(err);
      message.error(err.response?.data?.message || 'Error al enviar el correo');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: 'Eliminar borrador',
      content: '¿Estás seguro de que deseas eliminar este borrador de factura? Esta acción no se puede deshacer.',
      okText: 'Eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await userService.deleteInvoice(record.id);
          message.success('Borrador eliminado correctamente');
          fetchInvoices(buildFilters(uuidFilter, dateRange));
        } catch (error) {
          console.error('Error deleting invoice:', error);
          message.error(error.response?.data?.message || 'Error al eliminar el borrador');
        }
      },
    });
  };

  const handleClone = (record) => {
    navigate('/invoices/create', { state: { cloneFromId: record.id } });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'timbrada': return 'success';
      case 'cancelada': return 'error';
      case 'borrador': return 'warning';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'timbrada': return 'Timbrada';
      case 'cancelada': return 'Cancelada';
      case 'borrador': return 'Borrador';
      default: return 'Desconocido';
    }
  };

  const formatDateTime = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('es-MX', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getInvoiceDatesTooltip = (row) => (
    <div>
      <div>Fecha: {formatDateTime(row?.fecha)}</div>
      <div>Fecha timbrado: {formatDateTime(row?.fecha_timbrado)}</div>
    </div>
  );

  const columns = [
    {
      title: 'Serie/Folio',
      key: 'serie_folio',
      ellipsis: true,
      width: screens.xs ? 80 : 90,
      render: (_, row) => {
        const serie = row?.serie || '';
        const folio = row?.folio || '';
        const compact = [serie, folio].filter(Boolean).join('-') || '—';
        return (
          <Tooltip title={getInvoiceDatesTooltip(row)}>
            <span>{compact}</span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Cliente',
      dataIndex: 'client_name',
      key: 'client_name',
      ellipsis: true,
      width: screens.xs ? 120 : 220,
      render: (_, row) => {
        const rfc = row.receiver?.tax_id || 'N/A';
        const name = row.receiver?.tax_name || 'N/A';
        const label = screens.xs ? rfc : `${rfc} - ${name}`;
        return (
          <Tooltip
            title={
              <div>
                <div>{label}</div>
                {getInvoiceDatesTooltip(row)}
              </div>
            }
          >
            <span>{label}</span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Total / Saldo',
      dataIndex: 'total',
      key: 'total_balance',
      ellipsis: true,
      width: 100,
      render: (total, row) => {
        const totalNum = parseFloat(total || 0);
        const currency = row?.moneda || 'MXN';
        const metodo = row?.metodo_pago;

        if (metodo === 'PPD') {
          const paid = parseFloat(row.monto_pagado || 0);
          const balance = Math.max(totalNum - paid, 0);
          const tooltip = `$${totalNum.toFixed(2)} ${currency} | ${balance <= 0.01 ? 'Pagada' : `Debe: $${balance.toFixed(2)}`}`;
          return (
            <Tooltip title={tooltip}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <div style={{ whiteSpace: 'nowrap' }}>
                  ${totalNum.toFixed(2)}
                </div>
                <span style={{ fontSize: '12px', color: balance <= 0.01 ? '#389e0d' : '#cf1322', whiteSpace: 'nowrap' }}>
                  {balance <= 0.01 ? 'Pagada' : `Debe: $${balance.toFixed(2)}`}
                </span>
              </div>
            </Tooltip>
          );
        }

        return (
          <Tooltip title={`$${totalNum.toFixed(2)} ${currency}`}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ whiteSpace: 'nowrap' }}>
                ${totalNum.toFixed(2)}
              </div>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      responsive: ['md'],
      width: 90,
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {getStatusLabel(status)}
        </Tag>
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: screens.xs ? 110 : 140,
      render: (_, record) => (
        <Space size={2} wrap>
          <Tooltip title={record.status === 'borrador' ? "Seguir editando" : "Ver detalle"}>
            <Button
              shape="circle"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => {
                if (record.status === 'borrador') {
                  navigate('/invoices/create', { state: { invoiceId: record.id } });
                } else {
                  navigate(`/invoices/${record.id}`);
                }
              }}
            />
          </Tooltip>
          <Tooltip title={record.status === 'timbrada' ? 'Descargar PDF' : 'Ver PDF'}>
            <Button
              shape="circle"
              size="small"
              type="primary"
              icon={<FilePdfOutlined />}
              disabled={(record.status !== 'timbrada' && record.status !== 'borrador') || downloadingPdf[record.id]}
              loading={downloadingPdf[record.id]}
              onClick={() => handleQuickDownloadPdf(record)}
            />
          </Tooltip>
          <Dropdown
            trigger={['click']}
            menu={{
              items: [
                {
                  key: 'xml',
                  icon: <FileTextOutlined />,
                  label: 'Descargar XML',
                  disabled: record.status !== 'timbrada' || downloadingXml[record.id],
                  onClick: () => handleQuickDownloadXml(record),
                },
                {
                  key: 'email',
                  icon: <MailOutlined />,
                  label: 'Enviar por correo',
                  disabled: record.status === 'cancelada',
                  onClick: () => handleEmailClick(record),
                },
                {
                  key: 'clone',
                  icon: <CopyOutlined />,
                  label: 'Clonar factura',
                  onClick: () => handleClone(record),
                },
              ],
            }}
          >
            <Button shape="circle" size="small" icon={<MoreOutlined />} />
          </Dropdown>
          {record.status === 'borrador' && (
            <Tooltip title="Eliminar borrador">
              <Button
                shape="circle"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ].filter(item => !screens.xs || ['serie_folio', 'client_name', 'total_balance', 'actions'].includes(item.key));

  return (
    <div style={{ padding: screens.xs ? '12px' : '24px' }}>
      <div style={{ 
        display: 'flex', 
        flexDirection: screens.xs ? 'column' : 'row',
        flexWrap: 'wrap',
        justifyContent: 'space-between', 
        alignItems: screens.xs ? 'stretch' : 'center',
        gap: '12px',
        marginBottom: screens.xs ? '16px' : '24px'
      }}>
        <Title level={2} style={{ margin: 0, fontSize: screens.xs ? '20px' : '24px' }}>
          Mis Facturas
        </Title>
        <Space direction={screens.xs ? 'vertical' : 'horizontal'} style={{ width: screens.xs ? '100%' : 'auto' }}>
          <Button icon={<DownloadOutlined />} onClick={exportInvoicesCsv} loading={exportingCsv} block={screens.xs}>
            Exportar CSV
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size={screens.xs ? 'middle' : 'large'}
            onClick={() => navigate('/invoices/create')}
            block={screens.xs}
          >
            Nueva Factura
          </Button>
        </Space>
      </div>

      <div style={{ marginBottom: '16px' }}>
        <Card size="small" bodyStyle={{ padding: '12px' }}>
          <Row gutter={[12, 12]} align="middle">
            <Col xs={24} sm={12} md={6} lg={4}>
              <Select
                placeholder="Empresa"
                allowClear
                value={companyFilter}
                onChange={handleCompanyChange}
                style={{ width: '100%' }}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                }
              >
                {companies.map(company => (
                  <Select.Option key={company.id} value={company.id}>
                    {company.tax_name}
                  </Select.Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={6} lg={4}>
              <Select
                placeholder="Cliente"
                allowClear
                value={clientFilter}
                onChange={handleClientChange}
                style={{ width: '100%' }}
                showSearch
                filterOption={false}
                onSearch={handleClientSearch}
                notFoundContent={null}
                defaultActiveFirstOption={false}
                showArrow={false}
              >
                {clients.map(client => (
                  <Select.Option key={client.id} value={client.id}>
                    {client.tax_name || client.tax_id}
                  </Select.Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={6} lg={5}>
              <Input
                placeholder="Buscar por UUID"
                value={uuidFilter}
                onChange={handleUuidChange}
                allowClear
                style={{ width: '100%' }}
              />
            </Col>
            <Col xs={24} sm={12} md={6} lg={5}>
              <DatePicker.RangePicker 
                onChange={handleDateChange} 
                style={{ width: '100%' }}
                placeholder={['Inicio', 'Fin']}
              />
            </Col>
            <Col xs={12} sm={6} md={4} lg={2}>
              <Select
                placeholder="Estado"
                allowClear
                value={statusFilter}
                onChange={handleStatusChange}
                style={{ width: '100%' }}
              >
                <Select.Option value="timbrada">Timbrada</Select.Option>
                <Select.Option value="cancelada">Cancelada</Select.Option>
                <Select.Option value="borrador">Borrador</Select.Option>
              </Select>
            </Col>
            <Col xs={12} sm={6} md={4} lg={2}>
              <Select
                placeholder="Pago"
                allowClear
                value={paymentMethodFilter}
                onChange={handlePaymentMethodChange}
                style={{ width: '100%' }}
              >
                <Select.Option value="PUE">PUE</Select.Option>
                <Select.Option value="PPD">PPD</Select.Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={4} lg={2}>
              <Button 
                onClick={() => { 
                  setUuidFilter(''); 
                  setDateRange([]); 
                  setStatusFilter('timbrada'); // Reset to default
                  setCompanyFilter(null);
                  setPaymentMethodFilter(null);
                  setClientFilter(null);
                  // Forzar reset de filtros y página 1
                  fetchInvoices({ status: 'timbrada', uuid: '', date_from: null, date_to: null, company_id: null, metodo_pago: null, receiver_id: null }, 1, 10); 
                }}
                block
                icon={<FilterOutlined />}
              >
                Limpiar
              </Button>
            </Col>
          </Row>
        </Card>
      </div>

      <div ref={topPaginationRef} />
      <Table
        columns={columns}
        dataSource={invoices}
        loading={loading}
        rowKey="id"
        onChange={handleTableChange}
        tableLayout="fixed"
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showQuickJumper: true,
          position: bottomPaginationVisible ? ['topRight', 'bottomRight'] : ['topRight'],
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} de ${total} facturas`,
        }}
      />

      <Modal
        title="Vista previa del PDF"
        open={previewVisible}
        onCancel={() => {
          setPreviewVisible(false);
          if (pdfUrl) {
            window.URL.revokeObjectURL(pdfUrl);
            setPdfUrl(null);
          }
        }}
        footer={[
          <Button key="close" onClick={() => {
            setPreviewVisible(false);
            if (pdfUrl) {
              window.URL.revokeObjectURL(pdfUrl);
              setPdfUrl(null);
            }
          }}>
            Cerrar
          </Button>
        ]}
        width={1000}
        style={{ top: 20 }}
        bodyStyle={{ height: '80vh', padding: 0 }}
      >
        {pdfUrl && (
          <iframe
            src={pdfUrl}
            style={{ width: '100%', height: '100%', border: 'none' }}
            title="PDF Preview"
          />
        )}
      </Modal>

      <Modal
        title="Enviar Factura por Correo"
        open={emailModalVisible}
        onCancel={() => setEmailModalVisible(false)}
        footer={null}
        destroyOnClose
      >
        <Form form={emailForm} layout="vertical" onFinish={handleSendEmail}>
          <Form.Item
            label="Correo destinatario"
            name="recipient_email"
            rules={[
              { required: true, message: 'Ingresa el correo' },
              { type: 'email', message: 'Correo inválido' },
            ]}
          >
            <Input placeholder="correo@dominio.com" />
          </Form.Item>
          
          <Form.Item
            label="Asunto"
            name="subject"
            rules={[{ required: true, message: 'El asunto es requerido' }]}
          >
            <Input />
          </Form.Item>
          
          <Form.Item
            label="Mensaje"
            name="body"
          >
            <Input.TextArea rows={4} />
          </Form.Item>
          
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={sendingEmail} block>
              Enviar Correo
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserInvoices;
